// ────────────────────────────────────────────────────────────────────
// GuestInvitePanel — staff-only "เชิญให้ดู (guest)" widget.
//
// docs/superpowers/specs/2026-07-23-guest-access-design.md, frontend PR 2.
// Mounted on AssetWorkspaceScreen (scope_type="asset") and on
// SubmissionsScreen's Detail (scope_type="submission"). A single reusable
// component so the two surfaces can never drift on the ttl picker, the
// share-link UX, or the revoke flow.
//
// STAFF-ONLY — the caller is responsible for gating this behind isStaff
// (same convention as the Desk button on AssetWorkspaceScreen); this
// component does not re-check role itself since the backend already 403s a
// non-staff POST to /api/guest-invites and there is no user-facing harm in
// a customer never seeing the trigger.
// ────────────────────────────────────────────────────────────────────

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, X, Copy, Check, Ban } from "lucide-react";
import { apiClient, errMsg } from "@/lib/api";
import type { GuestInvite, GuestScopeType } from "@/lib/types";

const TTL_OPTIONS: readonly (readonly [number, string])[] = [
  [24, "1 วัน"], [168, "7 วัน"], [720, "30 วัน"],
];

function statusLabel(s: GuestInvite["status"]): { label: string; color: string } {
  if (s === "active") return { label: "ใช้งานอยู่", color: "var(--ok)" };
  if (s === "expired") return { label: "หมดอายุ", color: "var(--ink-3)" };
  return { label: "ยกเลิกแล้ว", color: "var(--err)" };
}

export function GuestInvitePanel({ scopeType, scopeRef, defaultLabel }: {
  scopeType: GuestScopeType;
  scopeRef: number;
  defaultLabel: string;
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [ttlHours, setTtlHours] = useState<number>(168);
  const [label, setLabel] = useState(defaultLabel);
  const [email, setEmail] = useState("");
  const [justCreated, setJustCreated] = useState<GuestInvite | null>(null);
  const [copied, setCopied] = useState(false);

  const listQ = useQuery({
    queryKey: ["guest-invites"],
    queryFn: () => apiClient.guestListInvites(),
    enabled: open,
    staleTime: 10_000,
  });
  const invites = useMemo(
    () => (listQ.data ?? []).filter((i) => i.scope_type === scopeType && i.scope_ref === scopeRef),
    [listQ.data, scopeType, scopeRef],
  );

  const create = useMutation({
    mutationFn: () => apiClient.guestCreateInvite({
      scope_type: scopeType, scope_ref: scopeRef,
      label: label.trim() || defaultLabel, invited_email: email.trim() || undefined,
      ttl_hours: ttlHours,
    }),
    onSuccess: (inv) => {
      setJustCreated(inv);
      setEmail("");
      qc.invalidateQueries({ queryKey: ["guest-invites"] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: number) => apiClient.guestRevokeInvite(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["guest-invites"] }),
  });

  const shareUrl = justCreated?.redeem_path ? `${window.location.origin}${justCreated.redeem_path}` : null;

  function copyLink(): void {
    if (!shareUrl) return;
    navigator.clipboard?.writeText(shareUrl).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1500); },
      () => prompt("คัดลอกลิงก์นี้:", shareUrl),
    );
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
              title="สร้างลิงก์ให้บุคคลภายนอกดูข้อมูลนี้แบบอ่านอย่างเดียว มีวันหมดอายุ"
              className="font-mono uppercase flex items-center gap-1.5 px-2 py-1 hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors"
              style={{ fontSize: 10.5, letterSpacing: ".16em", color: "var(--ink-3)", border: "1px solid var(--hairline)", cursor: "pointer" }}>
        <UserPlus size={11} />
        เชิญให้ดู (guest)
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[1200] flex items-start justify-center pt-[10vh]" style={{ background: "rgba(10,15,24,0.5)" }}
         onClick={() => { setOpen(false); setJustCreated(null); }}>
      <div className="w-[440px] max-w-[94vw] shadow-2xl" onClick={(e) => e.stopPropagation()}
           style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }}>
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--hairline)" }}>
          <div className="font-mono uppercase flex items-center gap-1.5" style={{ fontSize: 10, letterSpacing: ".16em", color: "var(--ink-3)" }}>
            <UserPlus size={12} /> เชิญให้ดู (guest)
          </div>
          <button onClick={() => { setOpen(false); setJustCreated(null); }} aria-label="ปิด">
            <X size={14} style={{ color: "var(--ink-4)" }} />
          </button>
        </div>

        <div className="p-4 flex flex-col gap-3">
          <p className="font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)", lineHeight: 1.5 }}>
            สร้างลิงก์ให้บุคคลภายนอกเห็นเฉพาะ{scopeType === "asset" ? "asset นี้" : "งานนี้"}แบบอ่านอย่างเดียว — ไม่เห็นข้อมูลอื่นในระบบ
          </p>

          {justCreated ? (
            <div className="p-3" style={{ border: "1px solid rgba(47,125,79,0.4)", background: "rgba(47,125,79,0.06)" }}>
              <div className="font-mono uppercase mb-1.5" style={{ fontSize: 8.5, letterSpacing: ".14em", color: "var(--ok)" }}>
                สร้างลิงก์แล้ว — คัดลอกตอนนี้ (จะไม่แสดงซ้ำอีก)
              </div>
              <div className="flex items-center gap-2">
                <input readOnly value={shareUrl ?? ""} onFocus={(e) => e.currentTarget.select()}
                       className="flex-1 font-mono px-2 py-1.5 outline-none" style={{ fontSize: 11, color: "var(--ink-1)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }} />
                <button onClick={copyLink}
                        className="font-mono uppercase inline-flex items-center gap-1 px-2 py-1.5 transition hover:brightness-95"
                        style={{ fontSize: 9.5, letterSpacing: ".08em", color: "#fff", background: "var(--ok)", border: "1px solid var(--ok)", fontWeight: 700 }}>
                  {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "คัดลอกแล้ว" : "คัดลอก"}
                </button>
              </div>
              <div className="font-mono mt-1.5" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
                หมดอายุ {new Date(justCreated.expires_at).toLocaleString()}
              </div>
            </div>
          ) : (
            <>
              <label className="flex flex-col gap-1">
                <span className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".16em", color: "var(--ink-4)" }}>คำอธิบาย (ผู้เชิญเห็น)</span>
                <input value={label} onChange={(e) => setLabel(e.target.value)}
                       className="font-mono px-2 py-1.5 outline-none" style={{ fontSize: 12, color: "var(--ink-1)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".16em", color: "var(--ink-4)" }}>อีเมล (ถ้ามี — บันทึกไว้เฉยๆ)</span>
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="inspector@doeb.go.th"
                       className="font-mono px-2 py-1.5 outline-none" style={{ fontSize: 12, color: "var(--ink-1)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }} />
              </label>
              <div className="flex flex-col gap-1">
                <span className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".16em", color: "var(--ink-4)" }}>อายุลิงก์</span>
                <div className="flex gap-1.5">
                  {TTL_OPTIONS.map(([h, lbl]) => (
                    <button key={h} onClick={() => setTtlHours(h)}
                            className="font-mono uppercase px-2.5 py-1 border transition-colors"
                            style={{ fontSize: 10, letterSpacing: ".06em",
                                     borderColor: ttlHours === h ? "var(--accent)" : "var(--hairline)",
                                     color: ttlHours === h ? "var(--accent-deep)" : "var(--ink-3)",
                                     background: ttlHours === h ? "rgba(var(--accent-rgb) / 0.08)" : "transparent" }}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              {create.isError && <div className="font-mono" style={{ fontSize: 11, color: "var(--err)" }}>{errMsg(create.error)}</div>}
              <button onClick={() => create.mutate()} disabled={create.isPending}
                      className="font-mono uppercase px-3.5 py-1.5 transition hover:brightness-95 active:translate-y-px disabled:opacity-40 self-end"
                      style={{ fontSize: 9.5, letterSpacing: ".1em", color: "var(--ink-1)", background: "var(--accent)", border: "1px solid var(--accent)", fontWeight: 700 }}>
                {create.isPending ? "กำลังสร้าง…" : "สร้างลิงก์"}
              </button>
            </>
          )}

          {/* ── ผู้เยี่ยมชม list ── */}
          <div className="mt-1 pt-3 border-t" style={{ borderColor: "var(--hairline)" }}>
            <div className="font-mono uppercase mb-2" style={{ fontSize: 8.5, letterSpacing: ".16em", color: "var(--ink-4)" }}>
              ผู้เยี่ยมชม · {invites.length}
            </div>
            {listQ.isPending && <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>กำลังโหลด…</div>}
            {listQ.isSuccess && invites.length === 0 && (
              <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>ยังไม่มีการเชิญ</div>
            )}
            {invites.length > 0 && (
              <div className="flex flex-col gap-1.5">
                {invites.map((inv) => {
                  const st = statusLabel(inv.status);
                  return (
                    <div key={inv.id} className="flex items-center gap-2 px-2.5 py-1.5" style={{ border: "1px solid var(--hairline)" }}>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono truncate" style={{ fontSize: 11, color: "var(--ink-1)" }}>{inv.label || "(ไม่มีคำอธิบาย)"}</div>
                        <div className="font-mono" style={{ fontSize: 9, color: "var(--ink-4)" }}>
                          หมดอายุ {new Date(inv.expires_at).toLocaleDateString()}
                          {inv.invited_email ? ` · ${inv.invited_email}` : ""}
                        </div>
                      </div>
                      <span className="font-mono uppercase shrink-0" style={{ fontSize: 8.5, letterSpacing: ".06em", color: st.color, fontWeight: 700 }}>{st.label}</span>
                      {inv.status === "active" && (
                        <button onClick={() => revoke.mutate(inv.id)} disabled={revoke.isPending}
                                title="เพิกถอนลิงก์นี้"
                                className="shrink-0 p-1 hover:text-[var(--err)] transition-colors disabled:opacity-40"
                                style={{ color: "var(--ink-3)" }}>
                          <Ban size={13} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
